import axios from "axios";
import i18n from "../i18n/i18n";
import { getActiveTenantSlug } from "../lib/tenantSlug";

const stageURL = "http://localhost:5001/api";

function normalizeBaseUrl(url) {
    return String(url || "").trim().replace(/\/+$/, "");
}

function resolveApiBaseUrl() {
    const fromEnv = normalizeBaseUrl(process.env.REACT_APP_API_BASE_URL);
    if (fromEnv) return fromEnv;

    if (process.env.NODE_ENV === "production") {
        console.warn("[apiUtils] REACT_APP_API_BASE_URL is not set – falling back to /api");
        return "/api";
    }
    return stageURL;
}

const ApiUtils = axios.create({
    baseURL: resolveApiBaseUrl(),
});


// ── Request interceptor — attach token ──
ApiUtils.interceptors.request.use((config) => {
    const token = localStorage.getItem("token");
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers["x-client-platform"] = "web";
    const tenantSlug = getActiveTenantSlug();
    if (tenantSlug) {
        config.headers["x-tenant-slug"] = tenantSlug;
    }
    return config;
});


// ── Refresh token logic ──
let _refreshPromise = null;

async function tryRefreshToken() {
    if (_refreshPromise) return _refreshPromise;

    const refreshToken = localStorage.getItem("refreshToken");
    const previousToken = localStorage.getItem("token");
    if (!refreshToken) return null;

    _refreshPromise = axios
        .post(`${resolveApiBaseUrl()}/Auth/Refresh`, { refreshToken }, { timeout: 15000, headers: { "x-client-platform": "web" } })
        .then((res) => {
            // A refresh started by a previous account must not replace a new login.
            if (localStorage.getItem("token") !== previousToken || localStorage.getItem("refreshToken") !== refreshToken) return null;
            if (res.status === 200 && res.data?.token) {
                localStorage.setItem("token", res.data.token);
                if (res.data.refreshToken) localStorage.setItem("refreshToken", res.data.refreshToken);
                window.dispatchEvent(new Event('lw-auth-changed'));
                return res.data.token;
            }
            return null;
        })
        .catch(() => null)
        .finally(() => { _refreshPromise = null; });

    return _refreshPromise;
}

function isPublicSigningApiRequest(config) {
    const url = String(config?.url || "");
    return /(?:^|\/)(?:SigningFiles|signing-batches)\/public\//i.test(url)
        || /(?:^|\/)signing-v2\/public(?:\/|$)/i.test(url);
}

function clearAuthAndRedirect() {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("role");
    localStorage.removeItem("isPlatformAdmin");
    window.dispatchEvent(new Event('lw-auth-changed'));

    // An expired office session must not interrupt a valid public signing link.
    // Background permission requests can fail while this independent flow is open.
    if (/^\/(?:PublicSignScreen|ViewSignedDocument|s)(?:\/|$)/i.test(window.location.pathname)) return;

    if (window.ReactNativeWebView?.postMessage) {
        window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'LOGOUT' }));
    }

    if (!window.location.pathname.includes('/LoginStack')) {
        window.location.href = '/LoginStack/LoginScreen';
    }
}


// ── Response helpers ──
function formatSuccess(response) {
    return { status: response.status, data: response.data || null, requestLink: response.config.url, success: true };
}

function translateAxiosMessage(error) {
    if (!error.response) {
        // Network-level failure: CORS, server down, no internet
        if (error.code === "ECONNABORTED" || /timeout/i.test(error.message)) {
            return i18n.t("errors.timeout");
        }
        return i18n.t("errors.networkError");
    }
    return error.message;
}

function formatError(error) {
    return { status: error.response?.status || 500, data: error.response?.data || null, requestLink: error.config?.url, success: false, message: translateAxiosMessage(error) };
}


// ── Response interceptor — handle 401 with silent refresh ──
ApiUtils.interceptors.response.use(
    (response) => formatSuccess(response),
    async (error) => {
        const status = error.response?.status || 500;
        const originalRequest = error.config;

        if (status === 503 && error.response?.data?.errorCode === 'SERVICE_TEMPORARILY_UNAVAILABLE') {
            window.dispatchEvent(new CustomEvent('lw-billing-locked', { detail: { locked: true } }));
        }
        if (status === 402 && (error.response?.data?.errorCode === 'BILLING_LOCKED' || error.response?.data?.billingLocked)) {
            window.dispatchEvent(new CustomEvent('lw-billing-locked', { detail: error.response.data || {} }));
        }

        if (status !== 401 || !originalRequest || originalRequest._retried || /(?:^|\/)Auth\/(?:RequestOtp|VerifyOtp|Login)(?:$|[?])/i.test(originalRequest.url || "")) {
            return formatError(error);
        }

        // Public signing endpoints use their own JWT — never treat 401 as session expiry.
        if (isPublicSigningApiRequest(originalRequest)) {
            return formatError(error);
        }

        const requestToken = String(originalRequest.headers?.Authorization || '').replace(/^Bearer\s+/i, '');
        // Concurrent startup requests may return 401 after another request has
        // already rotated the session. Retry with the current token instead of
        // rotating it again or logging out the newly authenticated account.
        const tokenAtFailure = localStorage.getItem('token');

        // Try refreshing the token once
        originalRequest._retried = true;
        const newToken = requestToken && tokenAtFailure && requestToken !== tokenAtFailure
            ? tokenAtFailure : await tryRefreshToken();

        if (!newToken) {
            if (localStorage.getItem('token') === tokenAtFailure) clearAuthAndRedirect();
            return formatError(error);
        }

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        try {
            return formatSuccess(await axios(originalRequest));
        } catch (retryErr) {
            if (retryErr.response?.status === 401 && !isPublicSigningApiRequest(retryErr.config) && localStorage.getItem('token') === newToken) {
                clearAuthAndRedirect();
            }
            return formatError(retryErr);
        }
    }
);

export default ApiUtils;
